<script setup>
import { useI18n } from 'vue-i18n';
import { usePiiRedaction } from 'dashboard/composables/usePiiRedaction';

const props = defineProps({
  people: { type: Array, default: () => [] },
  roleLabel: { type: String, default: '' },
  note: { type: String, default: '' },
});

defineOptions({ name: 'DetailPartyListCard' });

const { t } = useI18n();
const { redactPersonName, redactPhone, redactId, redactKra } =
  usePiiRedaction();

// Person fields are sometimes flat, sometimes nested under a wrapper key that
// varies by process (user_details, transferor, transferee, ...). Find
// whichever nested object actually looks like a person and merge it under
// the wrapper's own fields (e.g. transferor_id, status).
function unwrap(item) {
  if (item.names || item.name || item.full_name) return item;
  const nested = Object.values(item).find(
    v => v && typeof v === 'object' && !Array.isArray(v) && v.names
  );
  return nested ? { ...nested, ...item } : item;
}

function name(item) {
  const person = unwrap(item);
  return person.names || person.name || person.full_name || 'Unknown';
}

function initial(item) {
  return name(item).charAt(0).toUpperCase();
}

function role(item) {
  const person = unwrap(item);
  return (
    person.role ||
    person.user_role ||
    person.ownership_right ||
    props.roleLabel ||
    ''
  )
    .toString()
    .toUpperCase();
}

function userType(item) {
  const person = unwrap(item);
  const type = person.user_type || person.usertype;
  if (!type) return '';
  return type.charAt(0) + type.slice(1).toLowerCase();
}

function field(item, key) {
  return unwrap(item)[key];
}
</script>

<template>
  <div>
    <p v-if="note" class="mb-2 text-xs text-gray-500">{{ note }}</p>
    <div
      v-for="(person, index) in people"
      :key="person.id || index"
      class="mb-2 rounded-lg border border-gray-200 p-3 last:mb-0"
    >
      <div class="flex items-center gap-2">
        <span
          class="flex size-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600"
        >
          {{ initial(person) }}
        </span>
        <span class="text-sm font-medium text-gray-900">
          {{ redactPersonName(name(person)) }}
        </span>
        <span
          v-if="role(person)"
          class="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600"
        >
          {{ role(person) }}
        </span>
      </div>
      <p class="mt-1 pl-8 text-xs text-gray-500">
        <template v-if="userType(person)">{{ userType(person) }} · </template>
        <template v-if="field(person, 'phone_number')">
          {{ redactPhone(field(person, 'phone_number')) }} ·
        </template>
        {{ redactId(field(person, 'account_number')) }}
      </p>
      <p
        v-if="
          field(person, 'krapin') ||
          field(person, 'id_number') ||
          field(person, 'nationality')
        "
        class="pl-8 text-xs text-gray-500"
      >
        <template v-if="field(person, 'krapin')">
          {{ t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.KRA_LABEL') }}
          {{ redactKra(field(person, 'krapin')) }} ·
        </template>
        <template v-if="field(person, 'id_number')">
          {{ t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.ID_LABEL') }}
          {{ redactId(field(person, 'id_number')) }} ·
        </template>
        {{ field(person, 'nationality') }}
      </p>
    </div>
  </div>
</template>
